import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { sincronizacaoService, Tentativa } from '../../services/sincronizacao';

const TentativasTable: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [comando, setComando] = useState('');
  const [tentativas, setTentativas] = useState<Tentativa[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchTentativas = async () => {
      if (id) {
        setLoading(true);
        try {
          const result = await sincronizacaoService.getTentativasByIdSincronizacao(Number(id));
          setTentativas(result);
          if (result.length > 0) {
            setComando(result[0].comando);
          }
        } catch (error) {
          console.error('Erro ao buscar tentativas:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTentativas();
  }, [id]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVoltar = () => {
    navigate('/Sincronizacao');
  };

  // Paginação dos dados
  const paginatedTentativas = tentativas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Header com botão voltar */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Sincronização {id}</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleVoltar}
        >
          Voltar
        </Button>
      </Box>

      {/* Campo de comando */}
      <Box sx={{ p: 2 }}>
        <TextField
          label="Comando"
          value={comando}
          multiline
          rows={ 15 }
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </Box>

      {/* Tabela de tentativas */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Mensagem</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data e hora do processamento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedTentativas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma tentativa encontrada.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTentativas.map((tentativa) => (
                <TableRow key={tentativa.id} hover>
                  <TableCell>{tentativa.id}</TableCell>
                  <TableCell>{tentativa.mensagem}</TableCell>
                  <TableCell>{tentativa.dataProcessamentoString}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={tentativas.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default TentativasTable;
